import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

export interface Testimonial {
  image: string;
  name: string;
  profession: string;
  content: string;
}

interface TestimonialsSliderProps {
  testimonials: Testimonial[];
  className?: string;
}

const TestimonialsSlider: React.FC<TestimonialsSliderProps> = ({
  testimonials,
  className = '',
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setCurrentIndex((idx) => (idx + 1) % testimonials.length);
    }, 5000);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [currentIndex, testimonials.length]);

  const testimonial = testimonials[currentIndex];

  return (
    <div
      className={`w-full md:h-screen flex items-start md:items-center md:justify-center ${className} sticky top-0 z-20`}
    >
      <div className="w-full max-w-xl bg-transparent flex flex-col items-start p-8 shadow-none relative z-30">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            className="flex flex-col items-start w-full"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src={testimonial.image}
              alt={testimonial.name}
              className="w-12 h-12 rounded-full object-cover border-2 shadow-sm mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            />
            <motion.div
              className="text-2xl text-left font-medium mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              {testimonial.content}
            </motion.div>
            <motion.div
              className="text-base leading-6 text-left font-medium"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              {testimonial.name}
            </motion.div>
            <motion.div
              className="text-sm leading-5 text-left font-medium mb-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.3 }}
            >
              {testimonial.profession}
            </motion.div>
          </motion.div>
        </AnimatePresence>

        {/* Indicator */}
        <div className="flex items-start mt-8 w-full">
          {testimonials.map((_, idx) => {
            const isActive = idx === currentIndex;
            return (
              <motion.div
                key={idx}
                className="mx-1 bg-white rounded-full"
                style={{
                  height: 8,
                  width: isActive ? 24 : 8,
                  opacity: isActive ? 1 : 0.4,
                }}
                animate={{
                  width: isActive ? 24 : 8,
                  opacity: isActive ? 1 : 0.4,
                }}
                transition={{
                  duration: 0.7,
                  ease: 'easeInOut',
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TestimonialsSlider;
